import { useCallback, useEffect, useRef } from "react";

/**
 * Custom hook to debounce a function call.
 * @param func The function to debounce.
 * @param delay The debounce delay in milliseconds.
 * @returns A memoized function that will only execute the original function
 *          after the specified delay period has elapsed without any new calls.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useDebounce<F extends (...args: any[]) => any>(func: F, delay: number): (...args: Parameters<F>) => void {
  const funcRef = useRef<F>(func);

  // Ref to store the timeout ID
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update the funcRef.current whenever the func prop changes
  useEffect(() => {
    funcRef.current = func;
  }, [func]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delay]);

  const debouncedFunction = useCallback(
    (...args: Parameters<F>) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout
      timeoutRef.current = setTimeout(() => {
        funcRef.current(...args);
      }, delay);
    },
    [delay],
  );

  return debouncedFunction;
}

export default useDebounce;
